import { StatusBadge } from '@rankhelm/ui';
import type { ArticleStatus } from '@rankhelm/shared';

const STATUS_FLOW = [
  { from: 'draft', to: 'in_review', label: 'Submit for Review', color: 'bg-purple-600 hover:bg-purple-500' },
  { from: 'in_review', to: 'approved', label: 'Approve', color: 'bg-green-600 hover:bg-green-500' },
  { from: 'in_review', to: 'revision_requested', label: 'Request Revision', color: 'bg-orange-600 hover:bg-orange-500' },
  { from: 'revision_requested', to: 'in_review', label: 'Resubmit for Review', color: 'bg-purple-600 hover:bg-purple-500' },
  { from: 'approved', to: 'publishing', label: 'Publish to WordPress', color: 'bg-emerald-600 hover:bg-emerald-500' },
  { from: 'publish_failed', to: 'publishing', label: 'Retry Publish', color: 'bg-red-600 hover:bg-red-500' },
];

interface ArticleStatusBarProps {
  status: ArticleStatus;
  pillar?: string | null;
  targetKeyword?: string | null;
  wordCount: number;
  geoScore: number | null;
  updating: boolean;
  onStatusChange: (newStatus: string) => void;
}

export function ArticleStatusBar({
  status,
  pillar,
  targetKeyword,
  wordCount,
  geoScore,
  updating,
  onStatusChange,
}: ArticleStatusBarProps) {
  const availableTransitions = STATUS_FLOW.filter(t => t.from === status);

  return (
    <div className="mt-5 mb-6 flex gap-6 items-start">
      {/* GEO Score Circle */}
      {(geoScore ?? 0) > 0 && (
        <div className="flex flex-col items-center gap-2 shrink-0">
          <div
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-slate-900 shrink-0 border-[3px] ${
              (geoScore ?? 0) >= 80
                ? 'border-green-400'
                : (geoScore ?? 0) >= 60
                  ? 'border-yellow-300'
                  : 'border-red-400'
            }`}
          >
            <div className="text-center">
              <div className="text-[28px] font-bold text-slate-100">{geoScore}</div>
              <div className="text-[9px] text-slate-500">GEO</div>
              <div className="text-[10px] text-slate-500">/100</div>
            </div>
          </div>
        </div>
      )}

      {/* Info rows */}
      <div className="flex flex-col gap-1.5 flex-1">
        <div className="text-sm text-slate-200 font-medium">
          {pillar || targetKeyword || 'N/A'}
        </div>
        <div className="text-sm text-slate-400">
          {wordCount?.toLocaleString() || 0} words
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Action buttons */}
      {availableTransitions.length > 0 && (
        <div className="flex gap-2 shrink-0">
          {availableTransitions.map(t => (
            <button
              key={t.to}
              onClick={() => onStatusChange(t.to)}
              disabled={updating}
              className={`px-5 py-2.5 text-white border-none rounded-lg cursor-pointer text-sm font-semibold whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${t.color}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
